import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, FormAction, FormGrid } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { Notice, Section, Table, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

interface Campaign {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  batches: number;
  samples: number;
  responses: number;
  avg_overall: number | null;
  inventory_entries?: number;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}

/** Set by the tasting page after a delete, so this page can say what happened. */
export interface TastingsLocationState {
  deleted?: string;
}

/** DELETE sends no body, so the optional reason travels as ?reason= (the API reads both). */
export const withReason = (path: string, reason: string | undefined) =>
  reason ? `${path}?reason=${encodeURIComponent(reason)}` : path;

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** What goes with a tasting, in plain words, for the confirm dialog. */
export function tastingConsequences(c: {
  batches?: number;
  samples: number;
  responses: number;
}): string[] {
  return [
    c.batches ? plural(c.batches, 'batch', 'batches') : null,
    c.samples ? plural(c.samples, 'QR code slice', 'QR code slices') : null,
    c.responses ? plural(c.responses, 'guest rating', 'guest ratings') : null,
  ].filter((x): x is string => x !== null);
}

/** A short muted note in place of a Delete button the API would refuse. */
export function CantDelete({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 'var(--touch)',
        fontSize: 13,
        color: 'var(--ink-soft)',
      }}
    >
      {children}
    </span>
  );
}

export function Tastings() {
  const qc = useQueryClient();
  const { can } = useStaff();
  const canDelete = can('records.delete');
  const location = useLocation();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['tastings'],
    queryFn: async () => (await api.get<{ items: Campaign[] }>('/admin/tastings')).data.items,
  });
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<Campaign | null>(null);
  const deletedFromDetail = (location.state as TastingsLocationState | null)?.deleted;
  const [msg, setMsg] = useState<string | null>(
    deletedFromDetail ? `Deleted ${deletedFromDetail}.` : null,
  );
  // The message handed over by the tasting page is shown once; a reload should not repeat it.
  useEffect(() => {
    if (deletedFromDetail)
      void navigate(location.pathname, { replace: true, state: null, preventScrollReset: true });
  }, [deletedFromDetail, navigate, location.pathname]);
  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/admin/tastings', { name }),
    onSuccess: () => {
      setName('');
      setError(null);
      setMsg(null);
      void qc.invalidateQueries({ queryKey: ['tastings'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create.'),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tastings'] });
    void qc.invalidateQueries({ queryKey: ['feedback'] });
  };

  const head = ['Tasting', 'Active', 'Batches', 'Samples', 'Responses', 'Avg rating', 'Created'];
  return (
    <Section title="Tastings">
      <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
        Bake a batch for a tasting, hand out slices with a QR code, and see what people thought.
        Ingredients are taken out of inventory as sampling, at real cost.
      </p>
      {msg && <Notice kind="ok">{msg}</Notice>}
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          {/* The grid's last row carries 16px of bottom padding; the card already pads itself. */}
          <FormGrid min={320} style={{ marginBottom: -16 }}>
            <Field
              label="New tasting"
              placeholder="e.g. Winter Haven market, hot honey test"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              error={error ?? undefined}
              style={{ gridColumn: '1 / -2' }}
            />
            <FormAction>
              <Button type="submit" disabled={create.isPending}>
                Create
              </Button>
            </FormAction>
          </FormGrid>
        </form>
      </Card>
      {list.isError && <Notice kind="error">Could not load tastings.</Notice>}
      <Table
        head={canDelete ? [...head, ''] : head}
        stickyActions={canDelete}
        rowKeys={(list.data ?? []).map((c) => c.id)}
        rows={(list.data ?? []).map((c) => {
          const cells: ReactNode[] = [
            <Link to={`/tastings/${c.id}`}>{c.name}</Link>,
            c.is_active ? 'Yes' : 'No',
            String(c.batches),
            String(c.samples),
            String(c.responses),
            c.avg_overall === null ? 'No ratings' : `${c.avg_overall.toFixed(1)}/5`,
            when(c.created_at),
          ];
          if (canDelete)
            cells.push(
              c.can_delete === false ? (
                <CantDelete>Can’t delete, it used stock</CantDelete>
              ) : (
                <Button
                  variant="danger"
                  aria-label={`Delete ${c.name}`}
                  onClick={() => {
                    setMsg(null);
                    setTarget(c);
                  }}
                >
                  Delete
                </Button>
              ),
            );
          return cells;
        })}
        empty={list.isLoading ? 'Loading…' : 'No tastings yet.'}
      />
      {target && (
        <ConfirmDelete
          open
          onClose={() => setTarget(null)}
          title={`Delete ${target.name}?`}
          description={
            target.batches > 0
              ? 'Anything these batches took out of inventory stays recorded in inventory.'
              : 'This tasting has no batches yet.'
          }
          consequences={tastingConsequences(target)}
          askReason
          onConfirm={async ({ reason }) => {
            await api.delete(withReason(`/admin/tastings/${target.id}`, reason));
            setMsg(`Deleted ${target.name}.`);
            qc.removeQueries({ queryKey: ['tasting', target.id] });
            refresh();
          }}
          alternative={
            target.is_active
              ? {
                  label: 'Close feedback',
                  description:
                    'You can close feedback instead. The tasting and its results stay, and guests can no longer rate samples.',
                  pendingLabel: 'Closing…',
                  onClick: async () => {
                    await api.patch(`/admin/tastings/${target.id}`, { is_active: false });
                    setMsg(`Feedback closed for ${target.name}.`);
                    refresh();
                  },
                }
              : undefined
          }
        />
      )}
    </Section>
  );
}
